import React from 'react'
import cx from 'classnames'
import { Link } from 'gatsby'
import Layout from '../components/Layout'
import Image from '../components/Image'
import ElementWithWidth from '../components/ElementWithWidth'
import E from '../components/Element'
import ClassOnVisible from '../components/ClassOnVisible'
import BottomTaglineCubes from '../components/BottomTaglineCubes'
import OnMobile from '../components/OnMobile'
import { SEO } from '../components/SEO'

export const Head = () => (
  <SEO
    title="Profab Electronics | Careers: Help Us Build the Future of Tech"
    description="Join Profab's team of overachievers and work on world-changing projects in a modern, supportive environment. Browse our open positions and apply today!"
  />
)

const Careers = () => (
  <Layout>
    <HeroSection />
    <SectionLookingFor />
    <SectionProfabYou />
    <SectionModernOffices />
    <SectionFindMeaningfulWork />
    <SectionPartOfTeam />
    <ReadyToHelpUs />
  </Layout>
)

function HeroSection() {
  return (
    <div className="hero-container careers-hero-container dark-bg text-white">
      <div className="careers-hero-overlay" />
      <div className="container">
        <div className="flex">
          <div className="f1">
            <ElementWithWidth as="h1" width={620} className="hero-heading mb-2">
              Join our team, help us build the future
            </ElementWithWidth>
            <ClassOnVisible delay={200} insertClass="fadeInUp" startsInvisible>
              <ElementWithWidth
                width={380}
                as="div"
                className="hero-subtext text-semi-white mb-4"
              >
                Our customers entrust us with the enormous responsibility of
                manufacturing their products, which touch of the lives of
                countless people around the world.
              </ElementWithWidth>
            </ClassOnVisible>
            <ClassOnVisible delay={600} insertClass="fadeInUp" startsInvisible>
              <Link
                to="/team-openings"
                className="button button-wide button-blue"
              >
                See All Openings
              </Link>
            </ClassOnVisible>
          </div>
          <div className="f1" />
        </div>
      </div>
    </div>
  )
}

function SectionLookingFor() {
  return (
    <OnMobile>
      {matches => (
        <div
          className="section angled-bottom dark-bg text-white"
          style={{
            paddingTop: matches ? 40 : null,
            paddingBottom: 0,
            height: matches ? null : 394,
          }}
        >
          <div className="container">
            <div className="flex">
              <div className="f1">
                <ElementWithWidth
                  width={400}
                  as="h3"
                  className="section-heading mb-2"
                >
                  We're looking for exceptional people
                </ElementWithWidth>
                <ElementWithWidth
                  width={345}
                  as="p"
                  className="text-semi-white"
                >
                  We work with some of the world’s most innovative companies and
                  we’re proud of the role we play in their success. We’re
                  looking for overachievers to join us as we expand our reach
                  and global impact.
                </ElementWithWidth>
              </div>
              <div className="f1">
                <div className="careers-container">
                  <h4 className="text-medium mb-2">Team Openings</h4>
                  <div className="mb-2">
                    {[
                      {
                        slug: 'software-engineer',
                        name: 'Web Application Developer - Full Stack',
                      },
                      {
                        slug: 'account-executive',
                        name: 'Account Executive',
                      },
                      {
                        slug: 'operations-executive-assistant',
                        name: 'Operations Executive Assistant',
                      },
                      {
                        slug: 'factory-department-manager',
                        name: 'Factory Department Manager',
                      },
                      {
                        slug: 'quality-assurance-manager',
                        name: 'Quality Assurance Manager',
                      },
                      {
                        slug: 'quality-control-inspector',
                        name: 'Quality Control Inspector',
                      },
                      {
                        slug: 'machine-operator',
                        name: 'Machine Operator',
                      },
                    ].map(jobPosition => (
                      <JobPosition
                        key={jobPosition.slug}
                        position={jobPosition}
                      />
                    ))}
                  </div>
                  <Link className="see-all-openings-link" to="/team-openings">
                    See All Openings{' '}
                    <Image
                      name="blue-chevron-right"
                      width={8}
                      style={{
                        transform: 'translateX(5px) translateY(1px)',
                      }}
                    />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </OnMobile>
  )
}

function SectionProfabYou() {
  return (
    <OnMobile>
      {matches => (
        <div
          className="section blue-bg text-white text-center"
          style={{
            padding: matches ? '190px 0 200px' : '250px 0 300px',
          }}
        >
          <div className="container rel bring-to-top">
            <div className="heading-superscript">PRINCIPLES</div>
            <ElementWithWidth
              width={725}
              as="h3"
              center
              className="section-heading mb-7"
            >
              Profab + You
            </ElementWithWidth>
            <div className="flex">
              <ClassOnVisible
                className="f1"
                delay={0}
                insertClass="fadeInUp"
                startsInvisible
              >
                <UniqueFeature
                  light
                  image="icon-mission1"
                  width={40}
                  height={38}
                  title="Embrace openness"
                  text="You’ll constantly be presented with opportunities to collaborate on exciting and challenging new projects. Some of the best value we bring our customers comes from the ideas we share together."
                />
              </ClassOnVisible>
              <ClassOnVisible
                className="f1"
                delay={200}
                insertClass="fadeInUp"
                startsInvisible
              >
                <UniqueFeature
                  light
                  image="icon-mission2"
                  width={32}
                  height={38}
                  title="Challenge everything"
                  text="We build products for some of the most critical applications. Everything we can do to ensure reliability, no matter how big or small, is worth exploring. We’re always improving and challenging the status quo."
                />
              </ClassOnVisible>
              <ClassOnVisible
                className="f1"
                delay={400}
                insertClass="fadeInUp"
                startsInvisible
              >
                <UniqueFeature
                  light
                  image="icon-mission3"
                  width={36}
                  height={36}
                  title="Wear the customer’s shoes"
                  text="Spend the time to deeply understand customers and solve problems from their perspective. Earn trust through every interaction."
                />
              </ClassOnVisible>
            </div>
          </div>
          <div className="overlays">
            <div className="cube-bg careers-profab-left-cubes" />
            <div className="cube-bg careers-profab-right-cubes" />
          </div>
        </div>
      )}
    </OnMobile>
  )
}

function JobPosition(props) {
  const { position } = props
  return (
    <Link
      className="career-item"
      to={`/employment-application?position=${position.name}`}
    >
      <div className="job-position flex jcsb alc">
        <div>{position.name}</div>
        <div>
          <Image
            name="blue-chevron-right"
            width={10}
            height={15}
            style={{ transform: 'translateY(2px)' }}
          />
        </div>
      </div>
    </Link>
  )
}

function UniqueFeature(props) {
  const { image, title, text, width, height, light } = props
  return (
    <div className="f1 mh-2">
      <Image className="mb-1" name={image} width={width} height={height} />
      <h4 className="mb-1">{title}</h4>
      <p
        className={cx('mb-0', {
          'text-semi-white': light,
          'text-grayis': !light,
        })}
      >
        {text}
      </p>
    </div>
  )
}

function SectionModernOffices() {
  return (
    <OnMobile>
      {matches => (
        <div
          className="section angled-top angled-bottom white-bg"
          style={{
            paddingTop: matches ? 50 : null,
            paddingBottom: matches ? 360 : 300,
          }}
        >
          <div className="container rel bring-to-top">
            <div className="flex">
              <E
                as="div"
                className="mr-10"
                rules={{
                  transform: 'translateY(40px)',
                }}
              >
                <ElementWithWidth
                  width={408}
                  as="h3"
                  className="section-heading mb-2"
                >
                  Modern offices and manufacturing facilities
                </ElementWithWidth>
                <ElementWithWidth
                  width={345}
                  as="p"
                  className="text-grayish mb-4"
                >
                  We believe environment plays a huge role in a fulfilling work
                  experience. That’s why we’ve put a great deal of effort into
                  creating a modern, clean space that helps you feel your best.
                </ElementWithWidth>
              </E>
              <div className="f1 rel">
                <Image
                  name="careers-small-conference"
                  width={matches ? '75%' : 338}
                  type="jpg"
                  mobile={matches}
                  style={{
                    position: 'absolute',
                    bottom: -230,
                    left: 0,
                    zIndex: 2,
                    boxShadow: '8px 15px 20px 0 rgba(0,59,110,0.15)',
                  }}
                  mobileStyle={{
                    left: 20,
                    bottom: -320,
                  }}
                />
                <Image
                  name="factory-activity"
                  width={705}
                  mobile={matches}
                  responsive
                  style={{
                    position: 'absolute',
                    left: 100,
                    top: -50,
                    boxShadow: '8px 15px 20px 0 rgba(0,59,110,0.15)',
                  }}
                  mobileStyle={{
                    left: 0,
                    top: 0,
                  }}
                />
              </div>
            </div>
          </div>
          <div className="overlays">
            <div className="cube-bg careers-modern-offices-cubes" />
          </div>
        </div>
      )}
    </OnMobile>
  )
}

function SectionFindMeaningfulWork() {
  return (
    <OnMobile>
      {matches => (
        <div
          className="section dark-bg text-white"
          style={{
            paddingTop: matches ? 150 : 250,
          }}
        >
          <div className="container">
            <div className="text-center">
              <Image
                name="careers-logo"
                width={86}
                style={{
                  transform: 'translateX(10px)',
                }}
              />
              <div className="heading-superscript">WORK AT PROFAB</div>
              <h3 className="section-heading mb-3">
                Find the most meaningful work of your career
              </h3>
              <ElementWithWidth
                width={596}
                as="p"
                center
                className="text-semi-white"
              >
                If you’re inspired by the opportunity to play a role in the
                advancement of technology and work with some of the world’s most
                impactful companies, Profab is the right place for you.
              </ElementWithWidth>
            </div>
          </div>
        </div>
      )}
    </OnMobile>
  )
}

function SectionPartOfTeam() {
  return (
    <div className="section text-center" style={{ backgroundColor: '#F9FBFD' }}>
      <div className="container">
        <div className="heading-superscript text-light-blue">
          COMPANY BENEFITS FOR THE MODERN AGE
        </div>
        <ElementWithWidth
          width={551}
          center
          as="h3"
          className="section-heading mb-6"
        >
          Being part of the team comes with serious perks
        </ElementWithWidth>
        <div className="flex">
          <ClassOnVisible
            className="f1"
            delay={0}
            insertClass="fadeInUp"
            startsInvisible
          >
            <UniqueFeature
              image="icon-benefits-1"
              width={30}
              height={27}
              title="Wellbeing"
              text="We provide comfortable work environments with privacy and top of the line equipment. Not to mention we have awesome cappuccinos. ☕️"
            />
          </ClassOnVisible>
          <ClassOnVisible
            className="f1"
            delay={200}
            insertClass="fadeInUp"
            startsInvisible
          >
            <UniqueFeature
              image="icon-benefits-2"
              width={34}
              height={28}
              title="Healthcare"
              text="We are dedicated to helping our employees live longer and healthier lives through comprehensive health programs."
            />
          </ClassOnVisible>
          <ClassOnVisible
            className="f1"
            delay={400}
            insertClass="fadeInUp"
            startsInvisible
          >
            <UniqueFeature
              image="icon-benefits-3"
              width={58}
              height={21}
              title="Retirement"
              text="We offer programs to help our employees plan for their retirement needs including generous 401(K) matching. "
            />
          </ClassOnVisible>
        </div>
      </div>
    </div>
  )
}

function ReadyToHelpUs() {
  return (
    <div className="section blue-bg section-footer-tagline">
      <div className="container flex jcc rel bring-to-top">
        <div className="text-center text-white" style={{ padding: 30 }}>
          <h3 className="section-heading mb-1">
            Ready to help us build the future?
          </h3>
          <p className="text-semi-white mb-2">
            Browse our openings and apply today
          </p>
          <Link className="button button-wide button-white" to="/team-openings">
            See All Openings
          </Link>
        </div>
      </div>
      <BottomTaglineCubes />
    </div>
  )
}

export default Careers
